/* eslint-disable max-len */
import { Menu, MenuItem, TablePagination } from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import EditList from 'src/components/AccreditationEditList/AccreditationEditList';
import EmptyGridContent from 'src/components/EmptyGridContent/EmptyGridContent';
import TabContainer from 'src/components/TabContainer/TabContainer';
import {
  selectAccreditationEditItems,
  selectAccreditationLoading,
} from 'src/redux/slices/accreditationSlice';
import { fetchAccreditationEditItemsForCategory } from 'src/redux/thunks/accreditationThunks';
import { abortPromiseOnUnmount } from 'src/services/base.service';
import {
  AccreditationEditHeader,
  AccreditationEditListModel,
} from 'src/types/AccreditationEditList';
import { IconType } from 'src/types/propTypes/EmptyGridPropTypes';
import NavigationPage from '../../components/NavigationPage/NavigationPage';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { appRoutePaths } from '../../services/route.service';
import './AccreditationEdits.css';

const getFormattedDate = (): string => {
  const date = new Date();
  const year = date.getFullYear();
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}${month}${day}`;
};

const getTitleFromCategory = (category?: string): string => {
  switch (category?.toLowerCase()) {
    case 'school':
      return 'School Edits';
    case 'lea':
      return 'LEA Edits';
    case 'personnel':
      return 'Personnel Edits';
    default:
      return '';
  }
};

const AccreditationEdits = (): ReactElement => {
  const dispatch = useAppDispatch();
  const [title, setTitle] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [currTab, setCurrTab] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const isLoading = useAppSelector(selectAccreditationLoading);
  const edits: AccreditationEditListModel = useAppSelector(
    selectAccreditationEditItems
  );
  const { category } = useParams();

  useEffect(() => {
    if (category) {
      setCurrTab(0);
      setPage(0);
      const promise = dispatch(
        fetchAccreditationEditItemsForCategory(category)
      );
      return () => {
        abortPromiseOnUnmount(promise);
      };
    }
  }, [dispatch, category]);

  useEffect(() => {
    setTitle(getTitleFromCategory(category));
  }, [category]);

  const isEmptyList = Object.values(edits).flat().length === 0;

  const columes = Object.keys(edits).filter((key) => edits[key].length !== 0);

  const columnHeaders = (tabName: string): AccreditationEditHeader[] => {
    const keys = Object.keys(edits[tabName][0]);
    return keys.map((eachKey) => ({ key: eachKey, displayName: eachKey }));
  };

  const getExcelBuffer = async (): Promise<BlobPart> => {
    const workbook = new ExcelJS.Workbook();

    columes.forEach((tabName) => {
      const worksheet = workbook.addWorksheet(tabName.substring(0, 31));
      const headers = columnHeaders(tabName);

      worksheet.addRow(headers.map((header) => header.displayName));
      edits[tabName].forEach((row) => {
        worksheet.addRow(headers.map((header) => row[header.key] || ''));
      });
    });

    return await workbook.xlsx.writeBuffer();
  };

  const handleCloseMenu = (): void => {
    setAnchorEl(null);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLDivElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleExport = async (): Promise<void> => {
    const userFileName = prompt(
      'Enter file name',
      `${title} ${getFormattedDate()}`
    );
    handleCloseMenu();

    if (!userFileName) return;

    try {
      const excelBuffer = await getExcelBuffer();
      const fileBlob = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });
      saveAs(fileBlob, `${userFileName}.xlsx`);
    } catch (error) {
      console.error('Export failed:', error);
      alert(
        'Export failed. Please try again or contact support if the issue persists.'
      );
    }
  };

  const handleChangePage = (newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newLimit: number): void => {
    setLimit(newLimit);
    setPage(0);
  };

  const handleChangeTab = (newTab: number): void => {
    setCurrTab(newTab);
    setPage(0);
  };

  const buildTabs = (): ReactNode[] => {
    return columes.map((tab, index) => {
      return (
        <div
          key={`edit-${index}`}
          data-testid={`edit-${index}-tab-container`}
          className="accreditation-edit-container"
        >
          <EditList
            headerColumns={columnHeaders(tab)}
            dataRows={edits[tab].slice(
              page * limit,
              Math.min((page + 1) * limit, edits[tab].length)
            )}
          />
        </div>
      );
    });
  };

  return (
    <NavigationPage
      heading={title}
      pageClass="data-edits--detail"
      isLoading={isLoading}
      loadingDataId="data-edits-detail-loader"
      loadingText="Getting your details"
      backBarLocation={appRoutePaths.Accreditation}
    >
      {!isEmptyList && (
        <>
          <div className="accreditation-tools-container">
            <div className="right-side-tools">
              <TablePagination
                data-testid="accreditation-pagination"
                component="div"
                count={edits[columes[currTab]].length}
                page={page}
                onPageChange={(_e, newPage) => handleChangePage(newPage)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) =>
                  handleChangeRowsPerPage(Number(e.target.value))
                }
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
              />
              <div
                data-testid="accreditation-utils"
                className="three-dots"
                onClick={handleOpenMenu}
              >
                <span />
              </div>
            </div>
          </div>
          <TabContainer
            tab={currTab}
            handleChangeTab={handleChangeTab}
            tabNames={columes}
          >
            {buildTabs()}
          </TabContainer>
        </>
      )}

      {isEmptyList && (
        <EmptyGridContent iconType={IconType.FILE}>
          <p>There are currently no edits</p>
        </EmptyGridContent>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'accreditation-tools-menu',
        }}
      >
        <MenuItem data-testid="accreditation-export" onClick={handleExport}>
          Export
        </MenuItem>
      </Menu>
    </NavigationPage>
  );
};

export default AccreditationEdits;
